import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { UploadFile } from "@angular-ex/uploader";
import {
  FileUploaderComponent,
  UploaderAcceptFormat,
} from "../../../../shared/components/file-uploader/file-uploader.component";
import { UploaderResponseError } from "../../../../shared/components/file-uploader/models/uploader-response-error";
import { ByteUnit } from "../../../../shared/functions/byte-unit";
import { BrandCampaignAddContentDialogData } from "../../../../shared/models/brand-campaign-add-content-dialog-data";
import { BrandCampaign } from "../../../../shared/models/brandCampaign";
import { BrandCampaignCategory } from "../../../../shared/models/brandCampaignCategory";
import { BrandCampaignDetails } from "../../../../shared/models/brandCampaignDetail";
import { VideoEntity } from "../../../../shared/entities/file/video.entity";
import { AppDataService } from "../../../../shared/services/api/app-data.service";
import { cloneWith } from "../../../../shared/services/clonable";
import { NotificationService } from "../../../../shared/services/notification.service";
import { BrandCampaignService } from "../../shared/services/brand-campaign.service";
import { AnyToVideoEntityMapper } from "../../../../shared/mappers/video.entity.mapper";
import { unsafeCreatePartial } from "../../../../shared/utils/create-partial";

export interface CampaignContentFormVideoDialogData
  extends BrandCampaignAddContentDialogData {
  readonly video?: VideoEntity;
}

export type CampaignContentFormVideoDialogResult = VideoEntity | undefined;

@Component({
  selector: "app-campaign-content-form-video-dialog",
  templateUrl: "./campaign-content-form-video-dialog.component.html",
  styleUrls: ["./campaign-content-form-video-dialog.component.scss"],
})
export class CampaignContentFormVideoDialogComponent implements OnInit {
  public readonly uploadVideoUrl: string;
  public readonly uploaderFieldName = "video";
  public readonly acceptFormat = UploaderAcceptFormat.VideoMP4;
  public saving = false;
  public video: VideoEntity;
  public isNewVideo: boolean;
  public maxFileSizeInMegabytes?: number;
  public maxFileSizeInBytes?: number;
  public uploadData: any = {};
  public isSaveDisabled = false;
  public videoInQueue = false;

  @ViewChild(FileUploaderComponent)
  private fileUploaderComponent!: FileUploaderComponent;

  private readonly campaign: BrandCampaign;
  private readonly campaignDetails: BrandCampaignDetails;
  private readonly category: BrandCampaignCategory;

  public readonly fileUploaderResponseMapper = AnyToVideoEntityMapper;

  constructor(
    private readonly appDataService: AppDataService,
    private readonly translateService: TranslateService,
    private readonly brandCampaignService: BrandCampaignService,
    private readonly notificationService: NotificationService,
    private readonly dialogRef: MatDialogRef<
      CampaignContentFormVideoDialogComponent,
      CampaignContentFormVideoDialogResult
    >,
    @Inject(MAT_DIALOG_DATA) data: CampaignContentFormVideoDialogData,
  ) {
    this.campaign = data.campaign;
    this.campaignDetails = data.campaignDetails;
    this.category = data.category;
    this.isNewVideo = !data.video;

    if (!data.video) {
      this.video = unsafeCreatePartial(VideoEntity, {});

      this.uploadVideoUrl =
        this.brandCampaignService.getCreateCampaignDetailCategoryVideoUrl(
          this.campaign.id,
          this.campaignDetails.id,
          this.category.id,
        );
    } else {
      this.video = cloneWith(data.video);

      this.uploadVideoUrl =
        this.brandCampaignService.getUpdateCampaignDetailCategoryVideoUrl(
          this.campaign.id,
          this.campaignDetails.id,
          this.category.id,
          this.video.id,
        );
    }

    this.updateSaveDisabled();
  }

  public ngOnInit(): void {
    this.appDataService.get().then((appData) => {
      this.maxFileSizeInMegabytes = appData.maxUploadFileSize;
      this.maxFileSizeInBytes = ByteUnit.convert(
        this.maxFileSizeInMegabytes,
        ByteUnit.MB,
      );
    });
  }

  public saveContent(): void {
    this.saving = true;
    if (this.video.id) {
      this.uploadData.video_id = this.video.id.toString();
    }

    if (this.video.title) {
      this.uploadData.title = this.video.title;
    }

    this.updateSaveDisabled();
    if (this.videoInQueue) {
      this.fileUploaderComponent.startUploadAll();
    } else {
      this.saveVideo();
    }
  }

  private saveVideo(): void {
    this.brandCampaignService
      .updateCampaignDetailCategoryVideo(
        this.campaign.id,
        this.campaignDetails.id,
        this.category.id,
        this.video,
      )
      .subscribe({
        next: (video) => {
          this.video = video;
          this.dialogRef.close(this.video);
          if (this.isNewVideo) {
            this.category.videos.push(video);
          }
          this.notificationService.success("shared.changesSavedSuccessfully");
        },
        error: () => {
          this.saving = false;
          this.notificationService.error("shared.changesCouldNotBeSaved");
        },
      });
  }

  public handleUploadResponse(file: VideoEntity): void {
    this.video = file;
    this.saveVideo();
    this.notificationService.success("shared.videoUploadedSuccessfully");
  }

  public fileAddedToQueue(file: UploadFile): void {
    this.video = cloneWith(this.video, { videoName: file.name });
    this.videoInQueue = true;
    this.updateSaveDisabled();
  }

  public updateSaveDisabled(): void {
    this.isSaveDisabled =
      this.saving || !this.video.title || !this.video.videoName;
  }

  public fileUploaderErrorHandler(error: UploaderResponseError): void {
    this.notificationService.error(
      this.translateService.instant(error.message, error.interpolateParams),
    );
  }
}
